import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './custom/button.js';
import { Alert, AlertDescription, AlertTitle } from './alert.js';
import { Card, CardContent, CardFooter, CardHeader } from './card.js';

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  showDetails?: boolean;
  /**
   * Id del evento de Sentry. Se muestra siempre que exista: sin él, "nuestro
   * equipo ha sido notificado" no le sirve de nada a quien reporta el problema
   * — no hay forma de atar lo que vio en pantalla con el evento registrado.
   */
  eventId?: string;
}

export function ErrorFallback({
  error,
  resetError,
  showDetails = false,
  eventId,
}: ErrorFallbackProps) {
  const handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  const handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-destructive/10 p-3">
              <AlertTriangle className="h-10 w-10 text-destructive" />
            </div>
          </div>
          <h1 className="text-2xl font-bold">Algo salió mal</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Lo sentimos, ha ocurrido un error inesperado. Nuestro equipo ha sido
            notificado.
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {showDetails && error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Detalles del error</AlertTitle>
              <AlertDescription className="mt-2">
                <code className="text-xs break-all">{error.message}</code>
              </AlertDescription>
            </Alert>
          )}

          {eventId && (
            <div className="text-center text-xs text-muted-foreground">
              Código del error:{' '}
              <code className="font-mono select-all">{eventId}</code>
            </div>
          )}

          <div className="text-sm text-muted-foreground space-y-2">
            <p>Puedes intentar:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Recargar la página</li>
              <li>Volver a la página principal</li>
              <li>Contactar a soporte si el problema persiste</li>
            </ul>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col sm:flex-row gap-2  justify-center">
          {resetError && (
            <Button
              onClick={resetError}
              variant="outline"
              className="w-full sm:w-auto"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reintentar
            </Button>
          )}
          <Button
            onClick={handleReload}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Recargar página
          </Button>
          <Button onClick={handleGoHome} className="w-full sm:w-auto">
            <Home className="h-4 w-4 mr-2" />
            Ir al inicio
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
